import { useQuery } from "@apollo/client/react";
import { GetBrandQuery } from "@repo/graphql/documents/brand/query.generated";
import useChatDocuments from "@repo/components/src/hooks/agent/useChatDocuments";
import {
  CurrencyCode,
  toCurrencyCode,
} from "@repo/components/src/utils/money/currencies";
import { useGlobalSearchParams } from "expo-router";
import { createContext, ReactNode, useContext } from "react";

type BrandContextType = {
  brandId: string;
  brand?: GetBrandQuery["brand"]["get"];
  currency: CurrencyCode | null;
  countryCode?: string | null;
};

const BrandContext = createContext<BrandContextType | undefined>(undefined);

type BrandProviderProps = {
  children: ReactNode;
  /**
   * The brand to provide when it does not come from the `[brandId]` route segment —
   * the guest start's own brand on `/start`.
   */
  brandId?: string;
};

export const BrandProvider = ({
  children,
  brandId: brandIdProp,
}: BrandProviderProps) => {
  const params = useGlobalSearchParams<{ brandId: string }>();
  const brandId = brandIdProp ?? params.brandId;
  const documents = useChatDocuments();
  const { data } = useQuery(documents.brand, {
    variables: { id: brandId },
  });
  const country = data?.brand.get?.country;
  return (
    <BrandContext.Provider
      value={{
        brandId,
        brand: data?.brand.get,
        currency: toCurrencyCode(country?.currencyCode),
        countryCode: country?.code,
      }}
    >
      {children}
    </BrandContext.Provider>
  );
};

export const useBrandContext = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error("useBrandContext must be used within a BrandProvider");
  }
  return context;
};
